import type React from 'react';
import { useEffect } from 'react';

import { Stepper } from '@vlabs/uikit';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectAccountEmail } from '@vlabs/pages/auth/selectors';
import { selectDynamicHandlerId } from '@vlabs/pages/handlers/selectors';

import { StepCallbacksPolicy, StepPackageImport } from '@vlabs/monokit/entities/handlers/ui/steps';
import { useSteppedForm } from '@vlabs/monokit/shared/lib/use-stepped-form/useSteppedForm';

import type { TPackageImportFormValues } from './defaultFormValues';
import { defaultFormValues } from './defaultFormValues';

interface PackageImportFormProps {
  onSubmit: (values: TPackageImportFormValues) => void;
}

function PackageImportForm({
  onSubmit,
}: PackageImportFormProps): React.JSX.Element {
  const { t } = useTranslation();

  const dynamicHandlerId = useSelector(selectDynamicHandlerId);
  const accountEmail = useSelector(selectAccountEmail);
  const form = useForm<TPackageImportFormValues>({ defaultValues: defaultFormValues, mode: 'all' });

  const steps = [StepPackageImport, StepCallbacksPolicy];
  const stepper = useSteppedForm<TPackageImportFormValues>({ form, steps, onSubmit });

  const { setValue, handleSubmit } = form;

  useEffect(() => {
    setValue('content.handler.handler_id', dynamicHandlerId);
    setValue('description', `${accountEmail} packageImport`);
  }, [dynamicHandlerId, accountEmail, setValue]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h6>{t('features:packageImport.package_import_form.title')}</h6>
        <Stepper {...stepper}>
          {steps.map((Step) => <Step key={Step.name} />)}
        </Stepper>
      </form>
    </FormProvider>
  );
}

export { PackageImportForm };
